import { memo, useState } from 'react';
import styled from 'styled-components';
import { Flex, Text } from '@mantine/core';
import { BorderedBox } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/common/BorderedBox.tsx';
import { DocumentTypeBadge } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/common/DocumentTypeBadge.tsx';
import {
	TransMipCreate,
	useTranslationMipCreate,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { PEPModal } from '@dealstack/frontend/shared/components/PEPModal/PEPModal';

interface Props {
	className?: string;
}

const KycDeclarations = ({ className }: Props) => {
	const tMipCreate = useTranslationMipCreate();
	const [isPEPModalVisible, setIsPEPModalVisible] = useState(false);

	return (
		<div className={className}>
			<Text size="B5Bold" mb="0.5rem">
				{tMipCreate('kyc.declarations.title')}
			</Text>
			<Text size="B6Medium" c="dark.0" mb="0.5rem">
				{tMipCreate('kyc.declarations.subtitle')}
			</Text>
			<Flex gap="0.5rem" mt="1rem">
				<BorderedBox
					header={
						<Flex direction="column" gap="0.5rem">
							<Text size="B6Bold">
								{tMipCreate('kyc.declarations.individual_investors')}
							</Text>
							<Text size="B6Medium">
								{tMipCreate('kyc.declarations.will_be_asked_to')}
							</Text>
						</Flex>
					}
				>
					<Flex direction="column" gap="1rem">
						<Flex direction="column" gap="0.5rem">
							<DocumentTypeBadge
								text={tMipCreate('kyc.declarations.investor.beneficial_ownership')}
								iconVariant="signed"
							/>
							<Text size="B6Medium">
								{tMipCreate(
									'kyc.declarations.investor.beneficial_ownership_declaration'
								)}
							</Text>
						</Flex>
						<Flex direction="column" gap="0.5rem">
							<DocumentTypeBadge
								text={tMipCreate('kyc.declarations.source_funds.title')}
								iconVariant="signed"
							/>
							<Text size="B6Medium">
								{tMipCreate('kyc.declarations.source_funds.source_funds_are')}
							</Text>
							<ul style={{ listStyle: 'disc', paddingLeft: '1.5rem' }}>
								<li>
									<Text size="B6Medium">
										{tMipCreate('kyc.declarations.source_funds.salary')}
									</Text>
								</li>
								<li>
									<Text size="B6Medium">
										{tMipCreate('kyc.declarations.source_funds.inheritance')}
									</Text>
								</li>
								<li>
									<Text size="B6Medium">
										{tMipCreate(
											'kyc.declarations.source_funds.investment_income'
										)}
									</Text>
								</li>
								<li>
									<Text size="B6Medium">
										{tMipCreate(
											'kyc.declarations.source_funds.personal_savings'
										)}
									</Text>
								</li>
								<li>
									<Text size="B6Medium">
										{tMipCreate('kyc.declarations.source_funds.others')}
									</Text>
								</li>
							</ul>
						</Flex>
						<Flex direction="column" gap="0.5rem">
							<DocumentTypeBadge
								text={tMipCreate('kyc.declarations.pep')}
								iconVariant="signed"
							/>

							<ol style={{ listStyle: 'decimal', paddingLeft: '1.25rem' }}>
								<li>
									<Text size="B6Medium">
										{tMipCreate('kyc.declarations.pep_title')}
									</Text>
								</li>
								<li>
									<Text size="B6Medium">
										<TransMipCreate
											components={{
												open: (
													<Text
														component="span"
														c="blue.6"
														sx={{
															cursor: 'pointer',
														}}
														onClick={() => setIsPEPModalVisible(true)}
													/>
												),
											}}
											i18nKey="kyc.declarations.pep_subtitle"
										/>
									</Text>
								</li>
							</ol>
						</Flex>
					</Flex>
				</BorderedBox>
				<BorderedBox
					header={
						<Flex direction="column" gap="0.5rem">
							<Flex gap="0.5rem">
								<Text size="B6Bold">
									{tMipCreate('kyc.declarations.personal_investment_vehicle')}
								</Text>
								<Text size="B6Medium" c="dark.0">
									{tMipCreate('kyc.declarations.if_applicable')}
								</Text>
							</Flex>
							<Text size="B6Medium">
								{tMipCreate('kyc.declarations.will_be_asked')}
							</Text>
						</Flex>
					}
				>
					<Flex direction="column" gap="1rem">
						<Flex direction="column" gap="0.5rem">
							<DocumentTypeBadge
								text={tMipCreate('kyc.declarations.piv.beneficial_ownership')}
								iconVariant="signed"
							/>
							<Text size="B6Medium">
								{tMipCreate(
									'kyc.declarations.piv.beneficial_ownership_declaration'
								)}
							</Text>
						</Flex>
						<Flex direction="column" gap="0.5rem">
							<DocumentTypeBadge
								text={tMipCreate('kyc.declarations.source_funds.title')}
								iconVariant="signed"
							/>
							<Text size="B6Medium">
								{tMipCreate('kyc.declarations.source_funds.source_funds_are')}
							</Text>
							<ul style={{ listStyle: 'disc', paddingLeft: '1.5rem' }}>
								<li>
									<Text size="B6Medium">
										{tMipCreate('kyc.declarations.source_funds.salary')}
									</Text>
								</li>
								<li>
									<Text size="B6Medium">
										{tMipCreate('kyc.declarations.source_funds.inheritance')}
									</Text>
								</li>
								<li>
									<Text size="B6Medium">
										{tMipCreate(
											'kyc.declarations.source_funds.investment_income'
										)}
									</Text>
								</li>
								<li>
									<Text size="B6Medium">
										{tMipCreate(
											'kyc.declarations.source_funds.personal_savings'
										)}
									</Text>
								</li>
								<li>
									<Text size="B6Medium">
										{tMipCreate('kyc.declarations.source_funds.others')}
									</Text>
								</li>
							</ul>
						</Flex>
					</Flex>
				</BorderedBox>
			</Flex>
			{isPEPModalVisible && <PEPModal onClose={() => setIsPEPModalVisible(false)} />}
		</div>
	);
};

const Memoized = memo(KycDeclarations);
const Styled = styled(Memoized)`
	${DocumentTypeBadge} {
		width: 100%;
	}

	${BorderedBox} {
		height: fit-content;
	}
`;

export { Styled as KYCDeclarations };
